import { useState } from 'react';
import { Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase.js';
import { ROLES, CATEGORIES, type Role, type Category } from '@sfp/shared/roles';
import { ROLE_DEFAULT_PERMISSIONS } from '@sfp/shared/permissions';
import { useCollection, orderBy } from '../../lib/hooks.js';
import { useAuth } from '../../lib/auth.js';
import { DEFAULT_HOME_LAYOUT } from '../../lib/home-defaults.js';

interface UserRow {
  id: string;
  email?: string;
  displayName?: string;
  role?: Role;
  status?: string;
  allowedCategories?: Category[];
  allowedTileGroupIds?: string[];
  allowedSidebarItemIds?: string[];
  allowedTopActionIds?: string[];
  allowedTopicIds?: string[];
}

export function UsersAdmin() {
  const { role } = useAuth();
  const { docs, loading } = useCollection<UserRow>('users', [orderBy('email', 'asc')]);
  const [openId, setOpenId] = useState<string | null>(null);

  if (role !== 'admin') {
    return (
      <div className="mx-auto max-w-xl px-6 py-12 text-sm text-stone-500">
        Admins only.
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-6 py-8 space-y-6">
      <header className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-stone-400">Admin</div>
          <h1 className="text-2xl font-semibold text-stone-800">Users & permissions</h1>
          <p className="mt-1 text-sm text-stone-600">
            Click a row to edit per-user permissions, or{' '}
            <Link to="/admin/invite" className="text-[color:var(--sfp-primary)] hover:underline">
              invite a new user →
            </Link>
          </p>
        </div>
      </header>

      <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
        {loading ? (
          <div className="px-6 py-12 text-center text-sm text-stone-400">Loading…</div>
        ) : docs.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-stone-400">
            No users yet. <Link to="/admin/invite" className="text-[color:var(--sfp-primary)] hover:underline">Invite the first one →</Link>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-stone-200 bg-stone-50 text-left text-[10px] uppercase tracking-wider text-stone-500">
              <tr>
                <th className="px-4 py-2">Email</th>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Role</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {docs.map((u) => (
                <tr key={u.id} className="border-b border-stone-100 last:border-b-0">
                  <td className="px-4 py-2 font-mono text-xs text-stone-700">{u.email ?? u.id}</td>
                  <td className="px-4 py-2 text-stone-700">{u.displayName ?? '—'}</td>
                  <td className="px-4 py-2">
                    <span className="rounded bg-stone-100 px-2 py-0.5 text-[11px] capitalize text-stone-700">
                      {u.role?.replace(/_/g, ' ') ?? '—'}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-[11px] text-stone-500">{u.status ?? 'active'}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => setOpenId(openId === u.id ? null : u.id)}
                      className="text-xs text-[color:var(--sfp-primary)] hover:underline"
                    >
                      {openId === u.id ? 'Close' : 'Edit'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {openId &&
        (() => {
          const u = docs.find((x) => x.id === openId);
          if (!u) return null;
          return <PermissionsEditor key={u.id} user={u} onClose={() => setOpenId(null)} />;
        })()}
    </div>
  );
}

function PermissionsEditor({ user, onClose }: { user: UserRow; onClose: () => void }) {
  const [role, setRole] = useState<Role>(user.role ?? 'family_member');
  const [allowedCategories, setAllowedCategories] = useState<Category[]>(user.allowedCategories ?? []);
  const [allowedTileGroupIds, setAllowedTileGroupIds] = useState<string[]>(user.allowedTileGroupIds ?? []);
  const [allowedSidebarItemIds, setAllowedSidebarItemIds] = useState<string[]>(user.allowedSidebarItemIds ?? []);
  const [allowedTopicIds, setAllowedTopicIds] = useState<string[]>(user.allowedTopicIds ?? []);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  function applyRoleDefaults(r: Role) {
    setRole(r);
    const def = ROLE_DEFAULT_PERMISSIONS[r];
    if (def) {
      setAllowedTileGroupIds(def.allowedTileGroupIds);
      setAllowedSidebarItemIds(def.allowedSidebarItemIds);
      setAllowedTopicIds(def.allowedTopicIds);
    }
  }

  async function save() {
    setBusy(true);
    setMsg(null);
    try {
      const fn = httpsCallable(functions, 'setUserPermissions');
      await fn({
        userId: user.id,
        role,
        allowedCategories,
        allowedTileGroupIds,
        allowedSidebarItemIds,
        allowedTopActionIds: [],
        allowedTopicIds,
      });
      setMsg('Saved. Changes take effect on the user\'s next sign-in (or token refresh).');
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'Failed');
    } finally {
      setBusy(false);
    }
  }

  async function revoke() {
    if (!confirm(`Revoke ${user.email}? They'll be disabled and signed out.`)) return;
    setBusy(true);
    try {
      const fn = httpsCallable(functions, 'revokeUser');
      await fn({ userId: user.id });
      setMsg('Revoked.');
      onClose();
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'Failed');
    } finally {
      setBusy(false);
    }
  }

  const TOPICS = [
    { id: 'dropbox', label: 'Dropbox' },
    { id: 'sharepoint', label: 'SharePoint' },
    { id: 'vos', label: 'vOS' },
    { id: 'gmail', label: 'Gmail / Bills' },
    { id: 'plaid', label: 'Plaid' },
  ];

  return (
    <div className="space-y-4 rounded-2xl border border-stone-200 bg-white p-6 shadow-sm">
      <header>
        <h2 className="text-sm font-semibold text-stone-800">
          Edit permissions — <span className="font-mono">{user.email}</span>
        </h2>
        <div className="text-[10px] uppercase tracking-wider text-stone-400">UID {user.id}</div>
      </header>

      <label className="flex flex-col text-xs">
        <span className="text-stone-500">Role</span>
        <select
          value={role}
          onChange={(e) => applyRoleDefaults(e.target.value as Role)}
          className="rounded border border-stone-300 px-2 py-1.5 text-sm"
        >
          {ROLES.map((r) => (
            <option key={r}>{r}</option>
          ))}
        </select>
      </label>

      <Pane title="Tile groups" options={DEFAULT_HOME_LAYOUT.groups.map((g) => ({ id: g.id, label: g.title }))} selected={allowedTileGroupIds} onChange={setAllowedTileGroupIds} />

      <Pane
        title="Sidebar items"
        options={DEFAULT_HOME_LAYOUT.sidebar.flatMap((s) => s.items.map((it) => ({ id: it.id, label: it.label })))}
        selected={allowedSidebarItemIds}
        onChange={setAllowedSidebarItemIds}
      />

      <Pane
        title="Document categories"
        options={CATEGORIES.map((c) => ({ id: c, label: c.replace(/_/g, ' ') }))}
        selected={allowedCategories}
        onChange={(v) => setAllowedCategories(v as Category[])}
      />

      <Pane title="Apps / topics" options={TOPICS} selected={allowedTopicIds} onChange={setAllowedTopicIds} />

      <div className="flex items-center gap-3 pt-2">
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="rounded bg-[color:var(--sfp-primary)] px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {busy ? 'Saving…' : 'Save permissions'}
        </button>
        <button
          type="button"
          onClick={revoke}
          disabled={busy}
          className="rounded border border-[#9C4A3A] px-4 py-2 text-sm text-[#9C4A3A] hover:bg-[#9C4A3A]/10"
        >
          Revoke user
        </button>
        <button
          type="button"
          onClick={onClose}
          className="text-xs text-stone-500 hover:text-stone-800"
        >
          Close
        </button>
        {msg && <span className="text-[11px] text-stone-600">{msg}</span>}
      </div>
    </div>
  );
}

function Pane({
  title,
  options,
  selected,
  onChange,
}: {
  title: string;
  options: Array<{ id: string; label: string }>;
  selected: string[];
  onChange: (v: string[]) => void;
}) {
  const set = new Set(selected);
  return (
    <div>
      <div className="mb-1 text-[10px] uppercase tracking-wider text-stone-500">{title}</div>
      <div className="grid grid-cols-2 gap-1.5 md:grid-cols-3">
        {options.map((o) => (
          <label key={o.id} className="flex items-center gap-2 rounded border border-stone-200 bg-stone-50 px-2 py-1 text-xs">
            <input
              type="checkbox"
              checked={set.has(o.id)}
              onChange={(e) => {
                const next = new Set(selected);
                if (e.target.checked) next.add(o.id);
                else next.delete(o.id);
                onChange([...next]);
              }}
            />
            <span className="truncate">{o.label}</span>
          </label>
        ))}
      </div>
      {selected.length === 0 && (
        <div className="mt-1 text-[10px] italic text-stone-400">Empty = use role defaults.</div>
      )}
    </div>
  );
}
